/**
 * Easely Engine - Production Grade Orchestrator
 * v1.0.4 - Deep DOM Scanning & Context Awareness
 */
import { CONFIG } from './config';
import { registry } from './registry';
import { parser } from '../modules/parser';
import { animator } from '../modules/animator';
import { scrollEngine } from '../modules/scroll';

export const Engine = {
  /**
   * Main entry point for DOM discovery
   */
  scan() {
    // 1. Process Timelines First (Parent Containers)
    // We do this first so children are 'claimed' and ignored by the standalone scanner
    const timelines = document.querySelectorAll(`[${CONFIG.TL_PREFIX}]`);
    timelines.forEach(tlContainer => {
      if (!registry.isInitialized(tlContainer)) {
        this.initTimeline(tlContainer);
      }
    });

    // 2. Process Standalone Elements & Stagger Groups
    const elements = document.querySelectorAll(`[${CONFIG.PREFIX}]`);
    elements.forEach((el) => {
      const isRegistered = registry.isInitialized(el);
      const isPartOfTimeline = el.closest(`[${CONFIG.TL_PREFIX}]`);

      // Only initialize if it's not already running and not inside a data-ez-tl
      if (!isRegistered && !isPartOfTimeline) {
        const settings = parser.getSettings(el);
        this.initElement(el, settings);
      }
    });

    // 3. Sync with ScrollEngine to refresh markers
    scrollEngine.refresh();
  },

  /**
   * Orchestrates a sequence of animations within a container
   */
  initTimeline(container) {
    const gsap = animator.getGSAP();
    if (!gsap) return;

    const settings = parser.getSettings(container);
    const children = container.querySelectorAll(`[${CONFIG.PREFIX}]`);
    
    // Create the master GSAP Timeline instance
    const tl = gsap.timeline({
      scrollTrigger: settings.scroll ? {
        trigger: container,
        start: settings.scrollStart || "top 85%",
        toggleActions: "play none none none",
        scrub: settings.scrub
      } : null,
      repeat: settings.repeat || 0,
      yoyo: settings.yoyo || false,
      delay: settings.delay || 0
    });

    children.forEach((child, index) => {
      const childSettings = parser.getSettings(child);
      const overlap = child.getAttribute(`${CONFIG.PREFIX}-overlap`) || ">";
      
      // Create a paused tween for the timeline to control the playhead
      const tween = animator.createTween(child, childSettings);
      if (tween) tl.add(tween, overlap);
      
      // Mark child to prevent the standalone scanner from double-initializing it
      child.setAttribute(CONFIG.INIT_ATTR, 'ez-tl-child');
    });

    container.setAttribute(CONFIG.INIT_ATTR, 'ez-timeline');
    registry.register(container, tl);
  },

  /**
   * Initializes a single element or a staggered grid group
   */
  initElement(el, settings) {
    // Unique ID for internal tracking and registry
    const id = `ez_${Math.random().toString(36).substr(2, 5)}`;
    el.setAttribute(CONFIG.INIT_ATTR, id);

    const execute = () => {
      if (settings.stagger > 0) {
        this.runStagger(el, settings);
      } else {
        animator.animate(el, settings);
      }
    };

    // Orchestrate based on scroll presence
    if (settings.scroll) {
      scrollEngine.createTrigger(el, settings, execute);
    } else {
      execute();
    }
  },

  /**
   * Advanced Stagger Engine for direct children
   */
  runStagger(parent, settings) {
    const children = Array.from(parent.children);
    
    children.forEach((child, index) => {
      // Calculate individual delay based on stagger setting
      const childSettings = { 
        ...settings, 
        delay: (settings.delay || 0) + (index * settings.stagger) 
      };
      
      // Skip children already marked by timelines or previous scans
      if (!child.hasAttribute(CONFIG.INIT_ATTR)) {
        child.setAttribute(CONFIG.INIT_ATTR, 'ez-stagger-child');
        
        // Force hardware acceleration before animation starts
        child.style.willChange = 'transform, opacity';
        
        animator.animate(child, childSettings);
      }
    });
  }
};